import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Send, CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { BRAND } from "../../data/menu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENTS = ["Party Order", "Daily Tiffin", "Bulk Catering", "General Enquiry"];

export default function Enquiry() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    event_type: "Party Order",
    guests: "",
    date: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please share your name and phone number.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Dhanyavaad! We'll call you back shortly.");
      setForm({ name: "", phone: "", email: "", event_type: "Party Order", guests: "", date: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please call us instead.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "bg-[#f7f0e0] border-0 border-b-2 border-[#7a1f1f]/25 rounded-none px-0 h-11 focus-visible:ring-0 focus-visible:border-[#d97706] text-[#331818] placeholder:text-[#5c3a3a]/60";

  return (
    <section id="visit" className="relative bg-[#eae0c9] py-24 md:py-32">
      <div className="mx-auto max-w-6xl px-5 md:px-8 grid lg:grid-cols-2 gap-14 items-start">
        {/* Left copy */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <p className="text-sm uppercase tracking-[0.3em] font-bold text-[#d97706] mb-3">
            Order &amp; Enquire
          </p>
          <h2 className="font-display text-5xl md:text-6xl font-bold tracking-tight text-[#331818] leading-none">
            Bring Puneri Kitchen <span className="italic font-accent text-[#7a1f1f]">to your table</span>
          </h2>
          <p className="mt-6 text-lg text-[#5c3a3a] leading-relaxed max-w-md">
            Planning a get-together or want a daily tiffin plan? Drop your details
            and our team will call you back to craft the perfect spread.
          </p>

          <div className="mt-10 space-y-4">
            <a
              data-testid="enquiry-call"
              href={`tel:${BRAND.phone.replace(/\s/g, "")}`}
              className="flex items-center gap-4 group"
            >
              <span className="grid h-11 w-11 place-items-center rounded-full bg-[#7a1f1f] text-[#f7f0e0]">☎</span>
              <span className="font-display text-2xl font-semibold text-[#331818] group-hover:text-[#7a1f1f] transition-colors">
                {BRAND.phone}
              </span>
            </a>
            <a
              data-testid="enquiry-whatsapp"
              href={`https://wa.me/${BRAND.phoneRaw}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-[#1e5128] px-6 py-3 text-sm font-semibold text-[#f7f0e0] transition-colors duration-300 hover:bg-[#2b1414]"
            >
              Chat on WhatsApp
            </a>
          </div>
        </motion.div>

        {/* Form */}
        <motion.form
          data-testid="enquiry-form"
          onSubmit={submit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="rounded-3xl bg-[#f7f0e0] p-7 md:p-9 ring-1 ring-[#7a1f1f]/15 shadow-[0_20px_60px_rgba(122,31,31,0.10)]"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-[#5c3a3a]">Name*</Label>
              <Input data-testid="enquiry-name" className={inputCls} value={form.name} onChange={set("name")} placeholder="Your name" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-[#5c3a3a]">Phone*</Label>
              <Input data-testid="enquiry-phone" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="+91 ..." />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-[#5c3a3a]">Email</Label>
              <Input data-testid="enquiry-email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@email.com" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-[#5c3a3a]">Type</Label>
              <Select value={form.event_type} onValueChange={(v) => setForm((f) => ({ ...f, event_type: v }))}>
                <SelectTrigger data-testid="enquiry-type" className="bg-[#f7f0e0] border-0 border-b-2 border-[#7a1f1f]/25 rounded-none h-11 px-0 focus:ring-0 text-[#331818]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EVENTS.map((ev) => (
                    <SelectItem key={ev} value={ev} data-testid={`enquiry-type-${ev.toLowerCase().replace(/\s+/g, "-")}`}>
                      {ev}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-[#5c3a3a]">Guests</Label>
              <Input data-testid="enquiry-guests" className={inputCls} value={form.guests} onChange={set("guests")} placeholder="e.g. 25" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-[#5c3a3a]">Preferred Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    data-testid="enquiry-date"
                    className="flex h-11 w-full items-center justify-between border-0 border-b-2 border-[#7a1f1f]/25 bg-[#f7f0e0] text-left text-[#331818] focus:outline-none focus:border-[#d97706]"
                  >
                    <span className={form.date ? "" : "text-[#5c3a3a]/60"}>
                      {form.date ? format(new Date(form.date), "dd MMM yyyy") : "Pick a date"}
                    </span>
                    <CalendarIcon size={16} className="text-[#7a1f1f]" />
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-auto p-0 bg-[#f7f0e0]">
                  <Calendar
                    mode="single"
                    selected={form.date ? new Date(form.date) : undefined}
                    onSelect={(d) => setForm((f) => ({ ...f, date: d ? format(d, "yyyy-MM-dd") : "" }))}
                    disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
          <div className="mt-5 space-y-2">
            <Label className="text-xs uppercase tracking-widest text-[#5c3a3a]">Message</Label>
            <Textarea data-testid="enquiry-message" className="bg-[#f7f0e0] border-2 border-[#7a1f1f]/25 focus-visible:ring-0 focus-visible:border-[#d97706] text-[#331818]" rows={3} value={form.message} onChange={set("message")} placeholder="Tell us about your occasion..." />
          </div>
          <button
            data-testid="enquiry-submit"
            type="submit"
            disabled={loading}
            className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#7a1f1f] px-7 py-3.5 text-sm font-semibold text-[#f7f0e0] transition-colors duration-300 hover:bg-[#d97706] disabled:opacity-60"
          >
            {loading ? <Loader2 size={17} className="animate-spin" /> : <Send size={16} />}
            {loading ? "Sending..." : "Send Enquiry"}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
